import React, { useState, useEffect, useRef } from 'react';

import '../styles/Hero.css';

// Import illustration layers
import portraitCircle from '../images/home/div.col-lg-5.svg';
import figmaSketchColorWheel from '../images/home/Group 69.svg';
import adobeIcons from '../images/home/Adobe Creative Cloud Icons.svg';
import trainPhoto from '../images/home/Rectangle.svg';
import carPhoto from '../images/home/Group 70.svg';
import stickyNoteBottom from '../images/home/Sticky_-_1.svg';
import stickyNoteTrain from '../images/home/Sticky_-_1-1.svg';
import shadowGlow1 from '../images/home/Vector.svg';
import shadowGlow2 from '../images/home/Vector-1.svg';

function Hero() {
    const [mousePos, setMousePos] = useState({ x: 62, y: 24 });
    const [isHovered, setIsHovered] = useState(false);
    const containerRef = useRef(null);

    // Typewriter effect state for Figma Live Comment
    const welcomeMessage = "Hi there! Welcome to my portfolio 👋 Excited to collaborate!";
    const [typedText, setTypedText] = useState("");

    useEffect(() => {
        let index = 0;
        setTypedText("");

        const timer = setInterval(() => {
            if (index < welcomeMessage.length) {
                setTypedText(welcomeMessage.slice(0, index + 1));
                index++;
            } else {
                clearInterval(timer);
            }
        }, 40);

        return () => clearInterval(timer);
    }, []);

    const handleMouseMove = (e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePos({
            x: Math.max(5, Math.min(92, x)),
            y: Math.max(5, Math.min(92, y))
        });
    };

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    const cursorX = isHovered ? mousePos.x : 62;
    const cursorY = isHovered ? mousePos.y : 24;

    return (
        <div>
            <section className="hero">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 pe-lg-5 order-2 order-lg-1 mt-4 mt-lg-0">
                            <h1 className="display-3">Designing clarity <br /> at scale.</h1>
                            <div className="mt-4 pe-lg-5">
                                <p className="lead">
                                    I help organizations turn complex workflows into intuitive enterprise products used by millions of people. My background spans <b>UX design</b>, <b>systems thinking</b>, and <b>front-end engineering</b>, which allows me to move ideas from strategy to shipped product faster.
                                </p>
                                <div className="d-flex gap-3 mt-4">
                                    <a href="#portfolio" className="btn btn-dark btn-lg rounded-pill px-4">My Work</a>
                                    <a href="#skills" className="btn btn-outline-dark btn-lg rounded-pill px-4">My Skills</a>
                                </div>
                                <div className="mt-5 pt-3" style={{ paddingLeft: '50px' }}>
                                    <svg width="24" height="60" viewBox="0 0 24 60" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="4" r="3" fill="#1a1a1a"></circle>
                                        <line x1="12" y1="7" x2="12" y2="55"></line>
                                        <polyline points="19 48 12 55 5 48"></polyline>
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div
                            ref={containerRef}
                            className="col-lg-6 text-center hero-portrait-container order-1 order-lg-2 mb-4 mb-lg-0 position-relative"
                            onMouseMove={handleMouseMove}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            style={{ cursor: 'crosshair' }}
                        >
                            {/* Background Shadow/Glows */}
                            <img src={shadowGlow1} alt="" className="hero-layer hero-shadow-1" />
                            <img src={shadowGlow2} alt="" className="hero-layer hero-shadow-2" />

                            {/* Main Portrait Circle */}
                            <img src={portraitCircle} alt="Michelle Maltbia Portrait" className="img-fluid hero-portrait-image" />

                            {/* Figma/Sketch/Color Wheel */}
                            <img src={figmaSketchColorWheel} alt="Figma & Sketch" className="hero-layer hero-figma-sketch" />

                            {/* Adobe CC Icons */}
                            <img src={adobeIcons} alt="Adobe CC" className="hero-layer hero-adobe" />

                            {/* Train Photo */}
                            <img src={trainPhoto} alt="Train project" className="hero-layer hero-train" />

                            {/* Car Photo */}
                            <img src={carPhoto} alt="Car project" className="hero-layer hero-car" />

                            {/* Bottom Sticky Note */}
                            <img src={stickyNoteBottom} alt="Sticky note" className="hero-layer hero-sticky-bottom" />

                            {/* Train Sticky Note */}
                            <img src={stickyNoteTrain} alt="Train sticky note" className="hero-layer hero-sticky-train" />

                            {/* Interactive Figma Cursor & Live Comment Box */}
                            <div
                                className="hero-figma-cursor-wrapper"
                                style={{
                                    left: `${cursorX}%`,
                                    top: `${cursorY}%`,
                                    transition: isHovered
                                        ? 'left 0.2s cubic-bezier(0.25, 1, 0.5, 1), top 0.2s cubic-bezier(0.25, 1, 0.5, 1)'
                                        : 'left 0.7s ease, top 0.7s ease'
                                }}
                            >
                                <div className="hero-cursor-pointer-group">
                                    <svg width="24" height="24" viewBox="0 0 26 26" fill="none" className="figma-cursor-icon">
                                        <path
                                            d="M 4.5 3.5 C 3.5 2.7 2.4 3.8 3.2 4.8 L 9.8 24.2 C 10.3 25.4 11.9 25.3 12.3 24.0 L 15.1 15.1 L 24.0 12.3 C 25.3 11.9 25.4 10.3 24.2 9.8 L 4.8 3.2 C 4.6 3.1 4.4 3.1 4.5 3.5 Z"
                                            fill="#2563EB"
                                            stroke="#FFFFFF"
                                            strokeWidth="2"
                                            strokeLinejoin="round"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <div className="hero-cursor-badge">Michelle (Lead UX)</div>
                                </div>

                                {/* Figma Live Comment Box */}
                                <div className="figma-comment-box">
                                    <div className="figma-comment-header">
                                        <div className="figma-comment-avatar">MM</div>
                                        <div className="figma-comment-author">Michelle</div>
                                        <div className="figma-comment-time">just now</div>
                                    </div>
                                    <div className="figma-comment-body">
                                        {typedText}
                                        <span className="typing-cursor"></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default Hero;